import {
  ExchangeVariant,
  PendingReturnItem,
  ReturnableItem,
  ReturnProduct,
} from "@redotech/redo-model/draft-return/draft-return-items";
import { ReturnTypeEnum } from "@redotech/redo-model/return";
import {
  FieldId,
  InputAnswerPart,
  ReturnFlow,
  ReturnOption,
  Step,
} from "@redotech/redo-model/return-flow";
import {
  ReturnOptionMethod,
  StoreCreditType,
} from "@redotech/redo-model/return-flow/return-option";
import { ExchangeGroup } from "@redotech/redo-model/team";
import { ReturnAppSettings } from "../settings";

//For shared code between shop-mini frontend and customer-portal-app frontend

export type InputAnswerPartWithStep = InputAnswerPart & { step: number };
interface ItemStepData {
  flowPath: readonly number[];
  inputAnswers: readonly InputAnswerPartWithStep[];
  multipleChoiceAnswers: readonly {
    step: number;
    answer: number;
    fieldId?: FieldId;
  }[];
}

export type CompensationMethodData = Pick<
  PendingReturnItem,
  | "advancedExchangeItems"
  | "exchangeGroup"
  | "variantExchangeItem"
  | "expandedProductVariantExchanges"
  | "quantity"
>;

export type NewReturnItemData = ItemStepData &
  CompensationMethodData & { item: CombinedReturnItem; flow: ReturnFlow };

export enum StepType {
  FLOW = "flow",
  VARIANT_EXCHANGE = "variant-exchange",
  // For shop mini and 4.0
  EXCHANGE_GROUPS = "exchange-groups",
  SUGGESTED_EXCHANGE = "suggested-exchange",
  // for 3.5
  EXCHANGE_GROUP_SELECTED = "exchange-group-selected",
  EXCHANGE_GROUP_SELECTION = "exchange-group-selection",
  BUNDLE_TYPE = "bundle-type",
  ENSO_ENGRAVING = "enso-engraving",
}

export type ItemReturnFlowStep = ItemStepData &
  (
    | {
        type: StepType.FLOW;
        alreadySuggestedExchange?: true;
        preserveSuggestedExchange?: true;
      }
    | { type: StepType.VARIANT_EXCHANGE }
    | {
        type: StepType.EXCHANGE_GROUPS;
        exchangeGroups: ExchangeGroup[];
        suggested?: true;
      }
    | { type: StepType.SUGGESTED_EXCHANGE; exchangeGroups: ExchangeGroup[] }
    | { type: StepType.EXCHANGE_GROUP_SELECTED; exchangeGroup: ExchangeGroup }
    | { type: StepType.EXCHANGE_GROUP_SELECTION }
    | { type: StepType.BUNDLE_TYPE; options: ReturnOption[] }
    | { type: StepType.ENSO_ENGRAVING; variant: ExchangeVariant }
  );

export type CombinedReturnItem = ReturnableItem & {
  pendingItem?: PendingReturnItem;
  availableQuantity: number;
};

// Functions for getting return options on return step page

export enum ReturnOptionType {
  SAME_ITEM_EXCHANGE = "same-item-exchange",
  VARIANT_EXCHANGE = "variant-exchange",
  EXCHANGE_GROUPS = "exchange-groups",
  SHOP_OR_RETURN = "shop-or-return",
  BUNDLE_TYPE = "bundle-type",
}

export type ReturnStepPageOption =
  | { type: ReturnOptionType.SHOP_OR_RETURN; options: ReturnOption[] }
  | {
      type: ReturnOptionType.EXCHANGE_GROUPS;
      eligibleExchangeGroups: ExchangeGroup[];
    }
  | { type: ReturnOptionType.VARIANT_EXCHANGE }
  | { type: ReturnOptionType.SAME_ITEM_EXCHANGE; variant: ExchangeVariant }
  | { type: ReturnOptionType.BUNDLE_TYPE; options: ReturnOption[] };

export function getReturnStepPageOptions(
  returnStep: Step.Return,
  product: ReturnProduct,
  settings: ReturnAppSettings,
  returnableItem: ReturnableItem,
): ReturnStepPageOption[] {
  if (returnableItem?.isExpanded && returnableItem.products.length > 1) {
    return [
      { type: ReturnOptionType.BUNDLE_TYPE, options: returnStep.options },
    ];
  }

  const options: ReturnStepPageOption[] = [];
  const exchangeOptions = returnStep.options.filter(
    (option) => option.method.type === ReturnOptionMethod.EXCHANGE,
  );
  const sameItemVariant =
    exchangeOptions.filter((option) => option.exchangeTypes.sameItem).length &&
    getSameItemVariant(product, settings);

  !!sameItemVariant &&
    options.push({
      type: ReturnOptionType.SAME_ITEM_EXCHANGE,
      variant: sameItemVariant,
    });

  const canDoVariantExchange =
    exchangeOptions.filter((option) => option.exchangeTypes.variant).length &&
    product.variantData.variants.filter(
      (variant) =>
        String(variant.id) !== product.variantId &&
        isVariantInStock(variant, settings),
    ).length >= 1;

  canDoVariantExchange &&
    options.push({ type: ReturnOptionType.VARIANT_EXCHANGE });

  const eligibleExchangeGroups = getEligibleExchangeGroupsForProduct(
    product,
    settings.exchangeGroups,
  );
  const canDoExchangeGroups =
    exchangeOptions.some((option) => option.exchangeTypes.exchangeGroups) &&
    eligibleExchangeGroups.length > 0;

  canDoExchangeGroups &&
    options.push({
      type: ReturnOptionType.EXCHANGE_GROUPS,
      eligibleExchangeGroups,
    });

  const otherReturnOptions = getRefundReturnOptions(returnStep);

  otherReturnOptions.length &&
    options.push({
      type: ReturnOptionType.SHOP_OR_RETURN,
      options: otherReturnOptions,
    });

  if (options.length === 0 && returnStep.options[0]) {
    // something went wrong
    // either all exchange options are out of stock
    // or the return flow is misconfigured

    // give the shopper the option to return for store credit.
    options.push({
      type: ReturnOptionType.SHOP_OR_RETURN,
      options: [
        {
          ...returnStep.options[0],
          method: {
            type: ReturnOptionMethod.STORE_CREDIT,
            creditType: StoreCreditType.GIFT_CARD,
          },
        },
      ],
    });
  }

  return options;
}

export function getRefundReturnOptions(returnStep: Step.Return) {
  return returnStep.options.filter(
    (option) =>
      option.method.type !== ReturnOptionMethod.EXCHANGE ||
      option.exchangeTypes.advanced,
  );
}

// This will generate the same item variant for a return if the same item exchange option is available
// For a shipping claim or warranty claim, same item exchange is the only exchange option available
export function getSameItemVariant(
  product: ReturnProduct,
  settings: ReturnAppSettings,
) {
  return product.variantData.variants.find(
    (variant) =>
      String(variant.id) === product.variantId &&
      isVariantInStock(variant, settings),
  );
}

export const isVariantInStock = (
  variant: Pick<
    ExchangeVariant,
    "inventoryTracked" | "inventoryQuantity" | "inventoryPolicy"
  >,
  settings: ReturnAppSettings,
  ignoreStock: boolean = false,
) => {
  if (ignoreStock) {
    return true;
  }

  return (
    !variant.inventoryTracked ||
    continueIfOutOfStock(variant, settings) ||
    (variant.inventoryQuantity || 0) >= settings.inventory.minimum_stock
  );
};

export const continueIfOutOfStock = (
  variant: Pick<
    ExchangeVariant,
    "inventoryTracked" | "inventoryQuantity" | "inventoryPolicy"
  >,
  settings: ReturnAppSettings,
) => {
  return (
    settings.inventory.followShopifyInventoryPolicy &&
    variant.inventoryPolicy?.toLowerCase() === "continue"
  );
};

export function getEligibleExchangeGroupsForProduct(
  product: ReturnProduct,
  exchangeGroups: ExchangeGroup[] | undefined,
): ExchangeGroup[] {
  return (
    exchangeGroups?.filter((group) => {
      switch (group.type) {
        case "tag:tag":
        case "tag:collection": {
          return group.sourceTags.find((tag) =>
            product.variantData.tags.includes(tag),
          );
        }
        case "name:collection":
          return product.variantData.productTitle
            .toLowerCase()
            .includes(group.sourceName.toLowerCase());
        case "collection:collection":
          return product.collections.includes(group.sourceCollection.name);
        default:
          return false;
      }
    }) || []
  );
}

export function getGroupedReturnOptionsButtonText(
  returnOptions: ReturnOption[],
): string {
  const { exchange, refund, credit } = getReturnOptionBools(returnOptions);

  let str = exchange ? "Exchange for" : "Return for";
  let delim = "";

  if (exchange) {
    str += " a new product";
    delim = "or get";
  }
  if (refund) {
    str += ` ${delim} a refund`;
    delim = "or";
  }
  if (credit && (!refund || !exchange)) {
    str += ` ${delim} store credit`;
  }
  // Remove double spaces
  str = str.replace(/\s+/g, " ");
  return str;
}

function getReturnOptionBools(returnOptions: ReturnOption[]): {
  exchange: boolean;
  refund: boolean;
  credit: boolean;
} {
  let exchange = false;
  let refund = false;
  let credit = false;

  for (const option of returnOptions) {
    if (option.method.type === ReturnOptionMethod.EXCHANGE) {
      exchange = true;
      continue;
    }
    if (option.method.type === ReturnOptionMethod.STORE_CREDIT) {
      credit = true;
      continue;
    }
    if (option.method.type === ReturnOptionMethod.REFUND) {
      refund = true;
      continue;
    }
  }
  return { exchange, refund, credit };
}

export const isEnsoEngraving = (
  settings: ReturnAppSettings,
  product: ReturnProduct,
  returnType: ReturnTypeEnum,
): boolean => {
  const someVariantsAvailable = !!product.variantData.variants.some((variant) =>
    isVariantInStock(variant, settings),
  );
  const sameVariantAvailable = !!product.variantData.variants.find(
    (variant) =>
      String(variant.id) === String(product.variantId) &&
      isVariantInStock(variant, settings),
  );
  return (
    settings.storeUrl === "ensorings.myshopify.com" /* 😡 */ &&
    product.productProperties?.["text"] != null &&
    ((someVariantsAvailable && returnType === ReturnTypeEnum.RETURN) ||
      (sameVariantAvailable && returnType === ReturnTypeEnum.CLAIM))
  );
};

export const ENSORINGS_ENGRAVING_ID_PAID = "41268244152433";
export const ENSORINGS_ENGRAVING_ID_FREE = "41268244185201";
export const ENSO_RING_ENGRAVING_FEE = "8.99";
